import UseContext from "../../../hooks/useContext";
import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import ApiDatabase from "server";
import {toast} from "react-toastify";
import {dateDiff} from "../../../helper/helper";
import EmptyState from "../../../components/emptyState";
import {PositiveThumb, SearchIcon} from "../../../assets/icons";
import Pagination from "../../../components/pagination/pagination";
import StyledInput from "components/styledInput/StyledInput";
import SectionWrapper from "../components/SectionWrapper";

// Fixed action bar of the mission wizard (pages/createOfMission.js), the pagination line under
// the list, and the section's bottom padding: what must stay on screen below the list.
const WIZARD_FOOTER_HEIGHT = 81;
const PAGINATION_HEIGHT = 72;
const SECTION_BOTTOM_PADDING = 24;
const MIN_LIST_HEIGHT = 240;

const Invitation = () => {
    const listRef = useRef(null);
    const [listMaxHeight, setListMaxHeight] = useState(null);
    const {data, setData, companyData, language} = UseContext()
    const label = (key, defaultValue) =>
        language.getValue(`missionCreation.invitation.${key}`, defaultValue)
    const [tempWorkerRating, setTempWorkerRating] = useState(null);
    const [tempWorkerRatingSearchResult, setTempWorkerRatingSearchResult] = useState(null);
    const [tempWorkerRatingSearchFinalResult, setTempWorkerRatingSearchFinalResult] = useState(null);
    const [tempWorkerRatingSelected, setTempWorkerRatingSelected] = useState([]);
    const [loadingTempWorkerRating, setLoadingTempWorkerRating] = useState(false);
    const [search, setSearch] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const [countPerPage, setCountPerPage] = useState(0)
    const [countTotal, setCountTotal] = useState(0)

    useEffect(() => {
        if(companyData) {
            setLoadingTempWorkerRating(true)
            ApiDatabase.getTempWorkersRatingByMission({ idCompany: companyData._id, titleMission: data.qualification, dateStart: data.dateStart, dateEnd: data.dateEnd }, (response) => {
                setTempWorkerRating(response)
                setTempWorkerRatingSearchResult(response)
                setTempWorkerRatingSearchFinalResult(response)
                setLoadingTempWorkerRating(false)
            }, (err) => {
                toast.error('Une erreur est survenue, veuillez réessayer plus tard.')
            })
        }
    }, [companyData])

    useEffect(() => {
        if(tempWorkerRatingSelected.length === 0 && data.users_invite.length > 0) {
            setTempWorkerRatingSelected(data.users_invite)
        }
    }, [data])

    useEffect(() => {
        setData(prevData => ({
            ...prevData,
            users_invite: tempWorkerRatingSelected
        }))
    }, [tempWorkerRatingSelected])

    let timerTempWorkerRatingSuggestions = null;

    useEffect(() => {
        timerTempWorkerRatingSuggestions = setTimeout(async () => {
            await manageTempWorkerRatingSuggestions(search);
        }, 300);

        return () => clearTimeout(timerTempWorkerRatingSuggestions);
    }, [search])

    const manageTempWorkerRatingSuggestions = (search) => {
        if(tempWorkerRating?.length > 0) {
            setCurrentPage(1)
            const tempWorkerRatingSearchResult = []
            for (let i = 0; i < tempWorkerRating.length; i++) {
                if(tempWorkerRating[i].name.toLowerCase().includes(search.toLowerCase())) {
                    tempWorkerRatingSearchResult.push(tempWorkerRating[i])
                }
            }
            setTempWorkerRatingSearchResult(tempWorkerRatingSearchResult)
        }
    }

    useEffect(() => {
        if(tempWorkerRatingSearchResult){
            const maxCountPerPage = 15
            const countTotal = tempWorkerRatingSearchResult.length
            const totalPages = Math.ceil(countTotal / maxCountPerPage)
            //know the number of element in current page
            const countPerPage = (currentPage === totalPages) ? (countTotal - ((totalPages - 1) * maxCountPerPage)) : maxCountPerPage

            const startIndex = (currentPage - 1) * countPerPage;
            const endIndex = startIndex + countPerPage;

            const tempWorkerRatingSearchResultPaginated = tempWorkerRatingSearchResult.slice(
                startIndex,
                endIndex
            );

            setCountPerPage(countPerPage)
            setCountTotal(countTotal)
            setTotalPages(totalPages)

            setTempWorkerRatingSearchFinalResult(tempWorkerRatingSearchResultPaginated)
        }
    }, [tempWorkerRatingSearchResult, currentPage]);

    // The list takes the room left down to the pagination and scrolls inside, so the pagination
    // stays visible without scrolling the page.
    const hasPagination = !!(currentPage && totalPages > 1 && setCurrentPage);
    useLayoutEffect(() => {
        const measure = () => {
            const top = listRef.current?.getBoundingClientRect().top;
            if (top == null) return;
            const below = WIZARD_FOOTER_HEIGHT + SECTION_BOTTOM_PADDING + (hasPagination ? PAGINATION_HEIGHT : 0);
            setListMaxHeight(Math.max(MIN_LIST_HEIGHT, window.innerHeight - top - below));
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [hasPagination, loadingTempWorkerRating]);

    return (
        <SectionWrapper
            title={label('title', 'Re-travailler avec un profil ?')}
            description={label(
                'description',
                'Sélectionnez les profils avec lesquels vous souhaitez travailler pour cette mission, les profils sélectionnés seront placés en priorité. Uniquement les profils disponibles sont affichés.'
            )}
        >
            <StyledInput
                type={'text'}
                placeholder={label('searchPlaceholder', 'Nom ou prénom')}
                imgFront={<SearchIcon wh={18} color={'#6B7280'}/>}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />
            <div ref={listRef} className={'flex flex-col overflow-y-auto border rounded-lg'} style={listMaxHeight ? {maxHeight: listMaxHeight} : undefined}>
                    {loadingTempWorkerRating ? (
                        [1, 2, 3].map((i, index)=>(
                        <div key={index} className={'flex gap-4 px-5 py-4 border-b last:border-b-0'}>
                                <div className={'my-auto'}>
                                <div className="w-5 h-5 rounded border-2 border-gray-200 bg-gray-100 animate-pulse"></div>
                                </div>
                            <div className={'my-auto flex-1'}>
                                <div className={'h-4 bg-gray-200 rounded w-32 animate-pulse'}></div>
                                <div className={'h-3 bg-gray-100 rounded w-48 mt-2 animate-pulse'}></div>
                                    </div>
                                </div>
                        ))
                    ) : (
                        <>
                            {tempWorkerRatingSearchFinalResult && tempWorkerRatingSearchFinalResult.length > 0
                                ? tempWorkerRatingSearchFinalResult.map((tempWorker, index) => {
                                    let dateText = ''
                                    let dateEndMission = {}
                                    if(tempWorker.date_end) {
                                        dateEndMission = dateDiff(tempWorker.date_end)
                                        if(dateEndMission.day >= 0) {
                                            if(dateEndMission.day > 0) {
                                                dateText = `${dateEndMission.day} ${dateEndMission.day === 1 || dateEndMission.day === '1' ? 'jour' : 'jours'}`
                                            } else if(dateEndMission.hour > 0) {
                                                dateText = `${dateEndMission.hour} ${dateEndMission.hour === 1 || dateEndMission.hour === '1' ? 'heure' : 'heures'}`
                                            } else if(dateEndMission.min > 0) {
                                                dateText = `${dateEndMission.min} minutes`
                                            } else if(dateEndMission.sec > 0) {
                                                dateText = `${dateEndMission.sec} secondes`
                                            }
                                        } else {
                                            const dateAbs = Object.fromEntries(Object.entries(dateEndMission).map(([key, value]) => [key, Math.abs(value)]));
                                            if(dateAbs.day > 0) {
                                                dateText = `${dateAbs.day} ${dateAbs.day === 1 || dateAbs.day === '1' ? 'jour' : 'jours'}`
                                            } else if(dateAbs.hour > 0) {
                                                dateText = `${dateAbs.hour} ${dateAbs.hour === 1 || dateAbs.hour === '1' ? 'heure' : 'heures'}`
                                            } else if(dateAbs.min > 0) {
                                                dateText = `${dateAbs.min} minutes`
                                            } else if(dateAbs.sec > 0) {
                                                dateText = `${dateAbs.sec} secondes`
                                            }
                                        }
                                    }

                                const isSelected = tempWorkerRatingSelected.find((item) => item.id_user === tempWorker._id);

                                    return (
                                    <div 
                                        key={index} 
                                        className={`flex gap-4 justify-between cursor-pointer px-5 py-4 border-b last:border-b-0 transition-colors ${isSelected ? 'bg-yellow-50' : 'hover:bg-gray-50'}`}
                                        onClick={() => {
                                                               setTempWorkerRatingSelected((prevData) => {
                                                                   if(prevData.find((item) => item.id_user === tempWorker._id)) {
                                                                       return prevData.filter((item) => item.id_user !== tempWorker._id)
                                                                   } else {
                                                                       return [...prevData, {id_user: tempWorker._id, name: tempWorker.name}]
                                                                   }
                                                               });
                                                           }}
                                    >
                                        <div className={'flex gap-4 my-auto'}>
                                            <div className={'my-auto'}>
                                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                                    isSelected 
                                                        ? 'border-yellow-500 bg-yellow-500' 
                                                        : 'border-gray-300 bg-white'
                                                }`}>
                                                    {isSelected && (
                                                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    )}
                                                </div>
                                                </div>
                                                <div className={'my-auto'}>
                                                <div className="ft-b text-gray-900">{tempWorker.name}</div>
                                                    {dateText && (
                                                    <div className={'text-gray-500 ft-sm'}>
                                                            {tempWorker.title} {dateEndMission.day >= 0 ? 'il y a' : 'termine dans'} {dateText} {companyData._id !== tempWorker.id_company ? `chez ${tempWorker.company}` : ''}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        {tempWorker.isRated && (
                                                    <div className={'my-auto'}>
                                                        <div className={'rounded-full bg-green-50 p-2'}>
                                                            <PositiveThumb wh={20} color={'#10B981'}/>
                                                        </div>
                                                    </div>
                                        )}
                                    </div>
                                    )
                                })
                                : (
                                        <EmptyState type={'mission_invitation'} compact/>
                                )
                            }
                        </>
                    )}
                </div>
                {
                    hasPagination ? (
                    <div className={'py-4'}>
                            <Pagination currentPage={currentPage} countPerPage={countPerPage} countTotal={countTotal} totalPages={totalPages} setCurrentPage={setCurrentPage}/>
                        </div>
                    ) : null
                }
        </SectionWrapper>
    );
}

export default Invitation;
